import React from 'react';
import mapKeys from 'lodash/mapKeys';
import camelCase from 'lodash/camelCase';

import Colors from 'Wpds/colors/ColorConstants';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { unixDateMMMDDYYYY } from 'Shared/utils/DateTime.utils';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import './MeetingItemHistoryItem.scss';

type Props = {
  item: {
    userName: string;
    userId: number;
    timestamp: number;
    changeType: string;
    assigneeName: string | number;
    before?: number;
    after?: number;
  };
};

const getDetails = ({ item, tr }: { item: Props['item']; tr: (key: string, ...args: string[]) => string }) => {
  const standardizedItem = mapKeys(item, (v, k) => camelCase(k));
  const name = standardizedItem.userName;
  const timestamp = (
    <span className="MeetingItemHistoryItem__item__label__time">{unixDateMMMDDYYYY(standardizedItem.timestamp)}</span>
  );

  switch (standardizedItem.changeType) {
    case 'assigned':
      return (
        <div className="MeetingItemHistoryItem__item">
          <div className="MeetingItemHistoryItem__item__label">
            <span className="MeetingItemHistoryItem__item__label__bold">{name}</span>{' '}
            {tr('perform.meeting_item.history.assigned', `${standardizedItem.assigneeName}`)} - {timestamp}
          </div>
        </div>
      );
    case 'unassigned':
      return (
        <div className="MeetingItemHistoryItem__item">
          <div className="MeetingItemHistoryItem__item__label">
            <span className="MeetingItemHistoryItem__item__label__bold">{standardizedItem.assigneeName}</span>{' '}
            {tr('perform.meeting_item.history.unassigned_from', `${name}`)} - {timestamp}
          </div>
        </div>
      );
    case 'uncompleted':
      return (
        <div className="MeetingItemHistoryItem__item">
          <div className="MeetingItemHistoryItem__item__label">
            <span className="MeetingItemHistoryItem__item__label__bold">{name}</span>{' '}
            {tr('perform.meeting_item.history.marked_incomplete')} - {timestamp}
          </div>
        </div>
      );
    case 'completed':
      return (
        <div className="MeetingItemHistoryItem__item">
          <div className="MeetingItemHistoryItem__item__label">
            <span className="MeetingItemHistoryItem__item__label__bold">{name}</span>{' '}
            {tr('perform.meeting_item.history.marked_complete')} - {timestamp}
          </div>
        </div>
      );
    case 'changed_body':
      return (
        <div className="MeetingItemHistoryItem__item">
          <div className="MeetingItemHistoryItem__item__label">
            <span className="MeetingItemHistoryItem__item__label__bold">{name}</span>{' '}
            {tr('perform.meeting_item.history.edited_description')} - {timestamp}
          </div>
        </div>
      );
    case 'set_due_date':
    case 'unset_due_date':
      return (
        <div className="MeetingItemHistoryItem__item">
          <div className="MeetingItemHistoryItem__item__label">
            <span className="MeetingItemHistoryItem__item__label__bold">{name}</span>{' '}
            {tr('perform.meeting_item.history.changed_due_date')} - {timestamp}
          </div>
          <div className="MeetingItemHistoryItem__item__value">
            <span className="MeetingItemHistoryItem__item__value__before">
              {standardizedItem.before
                ? unixDateMMMDDYYYY(standardizedItem.before)
                : tr('perform.meeting_item.history.not_set')}
            </span>
            <span className="MeetingItemHistoryItem__item__value__arrow">
              <WpIcon name="arrow-right" color={Colors.gray[700]} />
            </span>
            <span className="MeetingItemHistoryItem__item__value__after">
              {standardizedItem.after
                ? unixDateMMMDDYYYY(standardizedItem.after)
                : tr('perform.meeting_item.history.not_set')}
            </span>
          </div>
        </div>
      );

    default:
      return false;
  }
};

const MeetingItemHistoryItem = ({ item }: Props) => {
  const { tr } = useTranslation();
  return <div className="MeetingItemHistoryItem">{getDetails({ item, tr })}</div>;
};

export default MeetingItemHistoryItem;
